/**
 * Staff console. Rendered only for accounts with is_staff — the server
 * enforces it regardless. Moderation queue, player lookup with audited
 * balance corrections, and the operational overview.
 */

import { useEffect, useState } from "react";
import type { JSX } from "react";
import { api, ApiError } from "./api";
import { useApp, type WindowId } from "./store";
import { Window } from "./Window";

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "something went wrong";
}

function useWindowChrome(id: WindowId) {
  const focused = useApp((s) => s.focused) === id;
  const closeWindow = useApp((s) => s.closeWindow);
  const focusWindow = useApp((s) => s.focusWindow);
  return { focused, onClose: () => closeWindow(id), onFocus: () => focusWindow(id) };
}

export function AdminWindow(): JSX.Element {
  const chrome = useWindowChrome("admin");
  const notify = useApp((s) => s.notify);
  const [tab, setTab] = useState<"overview" | "reports" | "players">("overview");
  const [overview, setOverview] = useState<Awaited<ReturnType<typeof api.adminOverview>> | null>(null);
  const [reports, setReports] = useState<Awaited<ReturnType<typeof api.adminReports>>["reports"]>([]);
  const [players, setPlayers] = useState<Awaited<ReturnType<typeof api.adminPlayers>>["players"]>([]);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async (): Promise<void> => {
    try {
      setOverview(await api.adminOverview());
      setReports((await api.adminReports()).reports);
    } catch (err) {
      notify("err", describe(err));
    }
  };
  useEffect(() => {
    void reload();
  }, []);

  const resolve = async (id: string, outcome: "resolved" | "dismissed"): Promise<void> => {
    setBusy(true);
    try {
      await api.adminResolveReport(id, outcome);
      await reload();
    } catch (err) {
      notify("err", describe(err));
    } finally {
      setBusy(false);
    }
  };

  const adjust = async (playerId: string, name: string): Promise<void> => {
    const amount = window.prompt(`Adjust ${name}'s wallet by (e.g. -100.00 or 250.00):`);
    if (!amount || !/^-?\d+\.\d{2}$/.test(amount)) return;
    const memo = window.prompt("Reason (audited):");
    if (!memo || memo.length < 5) return;
    setBusy(true);
    try {
      await api.adminAdjust(playerId, amount, memo);
      notify("ok", "Correction posted and audited.");
      setPlayers((await api.adminPlayers(query)).players);
    } catch (err) {
      notify("err", describe(err));
    } finally {
      setBusy(false);
    }
  };

  const tabs = (
    <div className="tabs" role="tablist">
      <button role="tab" aria-selected={tab === "overview"} onClick={() => setTab("overview")}>
        Overview
      </button>
      <button role="tab" aria-selected={tab === "reports"} onClick={() => setTab("reports")}>
        Reports <span className="count">{reports.length}</span>
      </button>
      <button role="tab" aria-selected={tab === "players"} onClick={() => setTab("players")}>
        Players
      </button>
    </div>
  );

  return (
    <Window title="Staff console" initial={{ x: 180, y: 90 }} width={560} maxHeight={560} tabs={tabs} {...chrome}>
      {tab === "overview" &&
        (overview === null ? (
          <p className="empty">Loading…</p>
        ) : (
          <div className="card">
            <div className="kv">
              <span className="k">Players</span>
              <span className="v">{overview.players.toLocaleString()}</span>
            </div>
            <div className="kv">
              <span className="k">Chunks</span>
              <span className="v small">
                {Object.entries(overview.chunks)
                  .map(([s, n]) => `${n} ${s}`)
                  .join(" · ") || "none yet"}
              </span>
            </div>
            <div className="kv">
              <span className="k">Open reports</span>
              <span className="v">{overview.openReports}</span>
            </div>
            <div className="kv">
              <span className="k">Ledger net total</span>
              <span className={`v ${overview.ledgerNetTotal === "0.00" ? "up" : "down"}`}>
                ${overview.ledgerNetTotal} {overview.ledgerNetTotal === "0.00" ? "(balanced)" : "(IMBALANCE!)"}
              </span>
            </div>
            <div className="kv">
              <span className="k">Settled through minute</span>
              <span className="v">{overview.settledThroughMinute.toLocaleString()}</span>
            </div>
          </div>
        ))}

      {tab === "reports" &&
        (reports.length === 0 ? (
          <p className="empty">The moderation queue is empty.</p>
        ) : (
          reports.map((r) => (
            <div className="card" key={r.id}>
              <div className="kv">
                <span className="k">
                  {r.reporterName} reported {r.subjectName ?? "—"}
                </span>
                <span className="v small faint">{new Date(r.createdAt).toLocaleString()}</span>
              </div>
              <p className="small" style={{ margin: "6px 0" }}>
                <span className="faint">{r.context}:</span> {r.body}
              </p>
              <div className="row-actions">
                <button disabled={busy} onClick={() => void resolve(r.id, "resolved")}>
                  Resolve
                </button>
                <button disabled={busy} onClick={() => void resolve(r.id, "dismissed")}>
                  Dismiss
                </button>
              </div>
            </div>
          ))
        ))}

      {tab === "players" && (
        <>
          <form
            style={{ display: "flex", gap: 8 }}
            onSubmit={(e) => {
              e.preventDefault();
              if (query.length > 0) {
                api.adminPlayers(query).then((r) => setPlayers(r.players), (err) => notify("err", describe(err)));
              }
            }}
          >
            <input
              style={{ flex: 1 }}
              placeholder="Name or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button type="submit">Search</button>
          </form>
          {players.map((p) => (
            <div className="card" key={p.id}>
              <div className="kv">
                <span className="k" style={{ fontWeight: 700, color: "var(--ink)" }}>
                  {p.displayName} {p.deleted && <span className="pill-tag">estate</span>}{" "}
                  {p.isStaff && <span className="pill-tag mine">staff</span>}
                </span>
                <span className="v">${p.wallet}</span>
              </div>
              <div className="kv">
                <span className="k small faint">{p.email}</span>
                <span className="v">
                  <button disabled={busy || p.deleted} onClick={() => void adjust(p.id, p.displayName)}>
                    Adjust balance
                  </button>
                </span>
              </div>
            </div>
          ))}
        </>
      )}
    </Window>
  );
}
